"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, Globe, ChevronDown, Mic, Video, MessagesSquare, ExternalLink, Check, Sun, Moon, Languages, Infinity as InfinityIcon, UserPlus } from "lucide-react";
import Link from "next/link";
import { useTheme } from "@/components/theme-provider";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import type { Lang } from "@/context/lang";
import { Logo } from "@/components/logo";

const LANGS: { code: Lang; label: string; flag: string; native: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸", native: "English" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷", native: "Türkçe" },
  { code: "es", label: "Español", flag: "🇪🇸", native: "Español" },
  { code: "bs", label: "Bosanski", flag: "🇧🇦", native: "Bosanski" },
  { code: "ru", label: "Русский", flag: "🇷🇺", native: "Русский" },
  { code: "de", label: "Deutsch", flag: "🇩🇪", native: "Deutsch" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [featDrop, setFeatDrop] = useState(false);
  const [langDrop, setLangDrop] = useState(false);
  const [user, setUser] = useState<{name?: string; email?: string} | null>(null);
  const [mounted, setMounted] = useState(false);
  const [studioLoading, setStudioLoading] = useState(false);
  const { lang, setLang, T } = useLang();
  const U = useUpper();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = mounted && resolvedTheme === "dark";
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    try {
      const data = localStorage.getItem("speakroar-user");
      if (data) setUser(JSON.parse(data));
    } catch {}
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangDrop(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];

  const featureItems = [
    { icon: Mic, label: T("nav.feat1.label"), desc: T("nav.feat1.desc"), href: "#ozellikler" },
    { icon: InfinityIcon, label: T("nav.feat5.label"), desc: T("nav.feat5.desc"), href: "#ozellikler" },
    { icon: Languages, label: T("nav.feat2.label"), desc: T("nav.feat2.desc"), href: "#ozellikler" },
    { icon: Video, label: T("nav.feat3.label"), desc: T("nav.feat3.desc"), href: "#ozellikler" },
    { icon: MessagesSquare, label: T("nav.feat4.label"), desc: T("nav.feat4.desc"), href: "#ozellikler" },
    { icon: UserPlus, label: T("nav.feat6.label"), desc: T("nav.feat6.desc"), href: "#ozellikler" },
  ];

  const openStudio = async () => {
    setStudioLoading(true);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://studio.speakroar.com";
    // Pencereyi tıklama anında (user-gesture içinde) senkron aç ki popup-blocker engellemesin;
    // hedef URL'yi token geldikten sonra ayarlıyoruz.
    const studioWin = window.open("", "_blank");
    const go = (url: string) => {
      if (studioWin) studioWin.location.href = url;
      else window.location.href = url;
    };
    try {
      const resp = await fetch("/api/auth/create-otp", { method: "POST" });
      const data = await resp.json();
      if (data.success && data.token) {
        go(`${apiUrl}/auto-login/${data.token}`);
      } else {
        go(apiUrl);
      }
    } catch {
      go(apiUrl);
    } finally {
      setStudioLoading(false);
    }
  };

  const handleLangChange = (code: Lang) => {
    setLang(code);
    setLangDrop(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/90 dark:bg-slate-950/85 backdrop-blur-xl shadow-sm border-b border-neutral-200/80 dark:border-white/5" : "bg-white/60 dark:bg-slate-950/60 backdrop-blur-md"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center">
            <Logo size="md" white={isDark} />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-1">
            <div className="relative" onMouseEnter={() => setFeatDrop(true)} onMouseLeave={() => setFeatDrop(false)}>
              <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5">
                {T("nav.features")} <ChevronDown size={14} className={`transition-transform ${featDrop ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {featDrop && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 shadow-xl p-2"
                  >
                    {featureItems.map((f, i) => (
                      <motion.a
                        key={f.label}
                        href={f.href}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04, duration: 0.2 }}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-lg bg-cyan-50 dark:bg-cyan-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <f.icon size={16} className="text-cyan-600 dark:text-cyan-300" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-neutral-900 dark:text-white">{f.label}</div>
                          <div className="text-xs text-neutral-500 dark:text-white/80 mt-0.5">{f.desc}</div>
                        </div>
                      </motion.a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href="#fiyatlar" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5">{T("nav.pricing")}</a>
            <a href="#karsilastir" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5">{T("nav.comparison")}</a>
            <a href="#sesler" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5">{T("nav.voices")}</a>
            <a href="#sss" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5">{T("nav.faq")}</a>
          </div>

          {/* Right */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Instagram */}
            <motion.a
              href="https://instagram.com/speakroar"
              target="_blank"
              rel="noopener noreferrer"
              whileTap={{ scale: 0.9 }}
              aria-label="Instagram"
              className="relative w-9 h-9 grid place-items-center rounded-full border border-neutral-200 bg-white/50 text-neutral-600 hover:border-pink-300 hover:bg-gradient-to-br hover:from-pink-50 hover:to-orange-50 hover:text-pink-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-pink-400/40 dark:hover:bg-white/10 dark:hover:text-pink-400 transition-all"
            >
              <i className="bi bi-instagram text-[15px]" />
            </motion.a>

            {/* Theme Toggle */}
            <motion.button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              whileTap={{ scale: 0.9 }}
              aria-label="Toggle theme"
              className="relative w-9 h-9 grid place-items-center rounded-full border border-neutral-200 bg-white/50 text-neutral-600 hover:border-cyan-300 hover:bg-cyan-50/50 hover:text-cyan-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-cyan-400/40 dark:hover:bg-white/10 dark:hover:text-cyan-300 transition-all"
            >
              <AnimatePresence mode="wait" initial={false}>
                {mounted && (
                  <motion.span
                    key={isDark ? "sun" : "moon"}
                    initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.25 }}
                    className="absolute inset-0 grid place-items-center"
                  >
                    {isDark ? <Sun size={15} /> : <Moon size={15} />}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Language Dropdown - Animated */}
            <div className="relative" ref={langRef}>
              <motion.button
                onClick={() => setLangDrop(!langDrop)}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-neutral-200 bg-white/50 text-xs font-semibold text-neutral-600 hover:border-cyan-300 hover:bg-cyan-50/50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-cyan-400/40 dark:hover:bg-white/10 transition-all duration-200 group"
              >
                <motion.span
                  key={currentLang.flag}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="text-base leading-none"
                >
                  {currentLang.flag}
                </motion.span>
                <motion.span
                  key={currentLang.code}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="font-bold"
                >
                  {currentLang.code.toUpperCase()}
                </motion.span>
                <ChevronDown size={11} className={`transition-transform duration-200 text-neutral-400 group-hover:text-cyan-500 ${langDrop ? "rotate-180" : ""}`} />
              </motion.button>

              <AnimatePresence>
                {langDrop && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.92 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-900 rounded-2xl border border-neutral-200/80 dark:border-white/10 shadow-2xl shadow-neutral-200/40 dark:shadow-black/40 py-2 z-50 overflow-hidden"
                  >
                    {/* Header */}
                    <div className="px-4 py-2 border-b border-neutral-100 dark:border-white/10">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-neutral-400 dark:text-slate-500 tracking-widest">
                        <Globe size={10} />
                        {U(T("nav.features").charAt(0) === "F" ? "Language" : T("nav.features").charAt(0) === "O" ? "Dil" : "Idioma")}
                      </div>
                    </div>

                    {LANGS.map((l, i) => (
                      <motion.button
                        key={l.code}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.06, duration: 0.25, ease: "easeOut" }}
                        onClick={() => handleLangChange(l.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150 ${
                          lang === l.code
                            ? "bg-gradient-to-r from-cyan-50 to-teal-50 text-cyan-700 dark:from-cyan-500/15 dark:to-teal-500/10 dark:text-cyan-300"
                            : "text-neutral-600 hover:bg-neutral-50 dark:text-slate-300 dark:hover:bg-white/5"
                        }`}
                      >
                        <motion.span
                          className="text-lg leading-none"
                          whileHover={{ scale: 1.2, rotate: [0, -10, 10, 0] }}
                          transition={{ duration: 0.3 }}
                        >
                          {l.flag}
                        </motion.span>
                        <div className="flex-1 text-left">
                          <div className={`font-semibold text-sm ${lang === l.code ? "text-cyan-700 dark:text-cyan-300" : "text-neutral-800 dark:text-slate-200"}`}>
                            {l.native}
                          </div>
                        </div>
                        {lang === l.code && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 15 }}
                          >
                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                              <Check size={10} className="text-white" strokeWidth={3} />
                            </div>
                          </motion.div>
                        )}
                      </motion.button>
                    ))}

                    {/* Bottom decoration */}
                    <div className="mt-1 mx-3 h-px bg-gradient-to-r from-transparent via-cyan-200 dark:via-cyan-400/30 to-transparent" />
                    <div className="px-4 py-2 text-center">
                      <span className="text-[9px] text-neutral-400 dark:text-slate-500 font-medium">
                        🌍 3 {lang === "tr" ? "dil mevcut" : lang === "es" ? "idiomas disponibles" : "languages available"}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {mounted && user ? (
              <>
                <Link href="/dashboard" className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 hover:border-cyan-200 dark:border-white/10 dark:hover:border-cyan-400/40 transition-all">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                    <span className="text-white text-[9px] font-bold">{user.name?.slice(0,2).toUpperCase() || "U"}</span>
                  </div>
                  <span className="text-sm font-semibold text-neutral-700 dark:text-white/90">{user.name?.split(" ")[0] || "User"}</span>
                </Link>
                <button
                  onClick={openStudio}
                  disabled={studioLoading}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  {studioLoading ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Mic size={13} />}
                  {T("nav.go_studio")} <ExternalLink size={11} />
                </button>
              </>
            ) : (
              <>
                <Link href="/login" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors">
                  {T("nav.login")}
                </Link>
                <Link
                  href="/register"
                  className="px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200"
                >
                  {T("nav.signup")}
                </Link>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex lg:hidden items-center gap-2">
            {/* Mobile Language Selector */}
            <div className="relative" ref={langRef}>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setLangDrop(!langDrop)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-neutral-200 bg-white/50 text-xs font-semibold text-neutral-600 hover:border-cyan-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-cyan-400/40 transition-all"
              >
                <motion.span
                  key={currentLang.flag}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="text-sm leading-none"
                >
                  {currentLang.flag}
                </motion.span>
                <motion.span
                  key={`m-${currentLang.code}`}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {currentLang.code.toUpperCase()}
                </motion.span>
              </motion.button>

              <AnimatePresence>
                {langDrop && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.9 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    className="absolute top-full right-0 mt-2 w-44 bg-white dark:bg-slate-900 rounded-2xl border border-neutral-200 dark:border-white/10 shadow-2xl dark:shadow-black/40 py-2 z-50"
                  >
                    {LANGS.map((l, i) => (
                      <motion.button
                        key={l.code}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.06 }}
                        onClick={() => handleLangChange(l.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${
                          lang === l.code ? "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300" : "text-neutral-600 hover:bg-neutral-50 dark:text-slate-300 dark:hover:bg-white/5"
                        }`}
                      >
                        <span className="text-lg">{l.flag}</span>
                        <span className={`font-semibold ${lang === l.code ? "text-cyan-700 dark:text-cyan-300" : "text-neutral-800 dark:text-slate-200"}`}>
                          {l.native}
                        </span>
                        {lang === l.code && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto">
                            <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                              <Check size={8} className="text-white" strokeWidth={3} />
                            </div>
                          </motion.div>
                        )}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile theme toggle */}
            <button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              aria-label="Toggle theme"
              className="w-9 h-9 grid place-items-center rounded-full border border-neutral-200 bg-white/50 text-neutral-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            >
              {mounted && isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button className="p-2 text-neutral-700 dark:text-white/90" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white dark:bg-slate-950 border-b border-neutral-200 dark:border-white/10"
          >
            <div className="px-4 py-4 space-y-2">
              {[
                { label: T("nav.features"), href: "#ozellikler" },
                { label: T("nav.pricing"), href: "#fiyatlar" },
                { label: T("nav.comparison"), href: "#karsilastir" },
                { label: T("nav.voices"), href: "#sesler" },
                { label: T("nav.faq"), href: "#sss" },
              ].map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.25 }}
                  className="block px-3 py-2.5 text-sm font-medium text-neutral-700 dark:text-white/90 rounded-lg hover:bg-neutral-50 dark:hover:bg-white/5"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </motion.a>
              ))}
              <div className="pt-2 space-y-2">
                {mounted && user ? (
                  <>
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{user.name?.slice(0,2).toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white">{user.name}</div>
                        <div className="text-xs text-neutral-500 dark:text-white/80">{user.email}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => { setOpen(false); openStudio(); }}
                      disabled={studioLoading}
                      className="w-full text-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full disabled:opacity-50"
                    >
                      {studioLoading ? "..." : T("nav.go_studio")}
                    </button>
                  </>
                ) : (
                  <>
                    <Link href="/login" className="block text-center px-4 py-2.5 text-sm font-medium text-neutral-700 dark:text-white/90 border border-neutral-200 dark:border-white/10 rounded-full" onClick={() => setOpen(false)}>{T("nav.login")}</Link>
                    <Link href="/register" className="block text-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full" onClick={() => setOpen(false)}>{T("nav.signup")}</Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
